import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Autocomplete,
  Chip,
  Avatar,
  ListItemText,
  ListItemAvatar,
  Alert
} from "@mui/material";

// Import icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import AppIcon from "@mui/icons-material/Apps";
import WebIcon from "@mui/icons-material/Web";
import MobileScreenShareIcon from "@mui/icons-material/MobileScreenShare";
import ApiIcon from "@mui/icons-material/Api";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Client } from '@microsoft/microsoft-graph-client';
import { useMsal } from '@azure/msal-react';

// Enhanced interface for application with search metadata
interface SearchableApplication {
  id: string;
  appId: string;
  displayName: string;
  signInAudience: string;
  appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown';
  hasSecrets: boolean;
  hasCredentials: boolean;
  tags: string[];
  searchText: string;
}

interface ManifestViewerProps {
  applications: any[];
  onBack?: () => void;
  onSelectApp?: (app: any) => void;
}

const ManifestViewer: React.FC<ManifestViewerProps> = ({ 
  applications, 
  onBack,
  onSelectApp
}) => {
  const { instance, accounts } = useMsal();
  
  // State for manifest JSON (read-only)
  const [manifestJson, setManifestJson] = useState<string>("");
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  
  // State for selected application
  const [selectedAppId, setSelectedAppId] = useState<string>("");

  // New state for enhanced search
  const [searchableApps, setSearchableApps] = useState<SearchableApplication[]>([]);
  const [selectedSearchApp, setSelectedSearchApp] = useState<SearchableApplication | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchTips, setShowSearchTips] = useState(false);

  const getGraphClient = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error("No active account found. Please sign in again.");
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.Read.All"], // Changed from ReadWrite to Read
        account: accounts[0]
      });

      return Client.init({
        authProvider: (done) => {
          done(null, response.accessToken);
        }
      });
    } catch (error: any) {
      console.error("Token acquisition failed:", error);
      
      if (error.name === "InteractionRequiredAuthError") {
        try {
          const response = await instance.acquireTokenPopup({
            scopes: ["Application.Read.All"], // Changed from ReadWrite to Read
            account: accounts[0]
          });
          
          return Client.init({
            authProvider: (done) => {
              done(null, response.accessToken);
            }
          });
        } catch (popupError) {
          throw new Error("Failed to authenticate. Please sign in again.");
        }
      }
      
      throw new Error("Authentication failed. Please try again.");
    }
  }, [instance, accounts]);

  // Process applications for enhanced search
  useEffect(() => {
    const processApplications = () => {
      const processed = applications.map(app => {
        let appType: 'Web' | 'SPA' | 'API' | 'Mobile' | 'Unknown' = 'Unknown';
        
        if (app.web?.redirectUris?.length > 0) {
          appType = 'Web';
        } else if (app.spa?.redirectUris?.length > 0) {
          appType = 'SPA';
        } else if (app.api?.oauth2PermissionScopes?.length > 0 || app.appRoles?.length > 0) {
          appType = 'API';
        } else if (app.publicClient?.redirectUris?.length > 0) {
          appType = 'Mobile';
        }

        const searchText = [
          app.displayName || '',
          app.appId || '',
          app.signInAudience || '',
          appType,
          ...(app.tags || []),
          ...(app.identifierUris || [])
        ].join(' ').toLowerCase();

        return {
          id: app.id,
          appId: app.appId || '',
          displayName: app.displayName || 'Unnamed Application',
          signInAudience: app.signInAudience || 'Unknown',
          appType,
          hasSecrets: (app.passwordCredentials?.length || 0) > 0,
          hasCredentials: (app.keyCredentials?.length || 0) > 0,
          tags: app.tags || [],
          searchText
        } as SearchableApplication;
      });

      processed.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setSearchableApps(processed);
    };

    if (applications.length > 0) {
      processApplications();
    }
  }, [applications]);

  // Helper functions for app type icons and colors (same as ManifestEditor)
  const getAppTypeIcon = (appType: string) => {
    switch (appType) {
      case 'Web':
        return <WebIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'SPA':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#9c27b0' }} />;
      case 'API':
        return <ApiIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
      case 'Mobile':
        return <MobileScreenShareIcon sx={{ fontSize: 16, color: '#388e3c' }} />;
      default:
        return <AppIcon sx={{ fontSize: 16, color: '#757575' }} />;
    }
  };

  const getAudienceColor = (audience: string) => {
    switch (audience) {
      case 'AzureADMyOrg':
        return '#1976d2';
      case 'AzureADMultipleOrgs':
        return '#f57c00';
      case 'AzureADandPersonalMicrosoftAccount':
        return '#9c27b0';
      case 'PersonalMicrosoftAccount':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  // Enhanced application selection handler (read-only)
  const handleEnhancedApplicationChange = async (
    event: React.SyntheticEvent,
    newValue: SearchableApplication | null
  ) => {
    if (!newValue) {
      setSelectedSearchApp(null);
      setSelectedApp(null);
      setSelectedAppId('');
      setManifestJson('');
      return;
    }

    setSearchLoading(true);
    setSelectedSearchApp(newValue);
    setSelectedAppId(newValue.id);
    
    try {
      const basicApp = applications.find(a => a.id === newValue.id);
      if (basicApp) {
        setSelectedApp(basicApp);
        
        const client = await getGraphClient();
        
        console.log(`👁️ Manifest Viewer: Loading manifest for ${newValue.displayName}...`);
        
        const fullAppResponse = await client.api(`/applications/${newValue.id}`)
          .select([
            'id',
            'appId',
            'displayName',
            'identifierUris',
            'web',
            'spa',
            'publicClient',
            'api',
            'appRoles',
            'requiredResourceAccess',
            'passwordCredentials',
            'keyCredentials',
            'signInAudience',
            'groupMembershipClaims',
            'optionalClaims',
            'oauth2RequirePostResponse',
            'tags',
            'tokenEncryptionKeyId',
            'isFallbackPublicClient',
            'isDeviceOnlyAuthSupported',
            'notes',
            'defaultRedirectUri',
            'description'
          ].join(','))
          .get();

        console.log(`✅ Manifest Viewer: Manifest loaded for ${newValue.displayName}`);
        
        // Create response for viewing (including read-only properties for completeness)
        setSelectedApp(fullAppResponse);
        
        // Format as pretty JSON
        const prettyJson = JSON.stringify(fullAppResponse, null, 2);
        setManifestJson(prettyJson);
        
        if (onSelectApp) {
          onSelectApp(fullAppResponse);
        }
      }
    } catch (err: any) {
      console.error('❌ Manifest Viewer: Error fetching application manifest:', err);
      
      // Fallback to basic app data
      const basicApp = applications.find(a => a.id === newValue.id);
      if (basicApp) {
        setSelectedApp(basicApp);
        setManifestJson(JSON.stringify(basicApp, null, 2));
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Custom filter function for better search
  const filterApplications = (options: SearchableApplication[], { inputValue }: { inputValue: string }) => {
    if (!inputValue.trim()) {
      return options.slice(0, 50);
    }

    const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);
    
    return options.filter(app => {
      return searchTerms.every(term => app.searchText.includes(term));
    }).slice(0, 100);
  };  

  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(manifestJson);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Application Manifest Viewer
              <Chip 
                label="Read-Only" 
                size="small" 
                sx={{ 
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 600
                }} 
              />
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              View application manifest configuration (read-only)
            </Typography>
          </Box>
        </Box>
      </Card>
      
      {/* Enhanced Application Selection Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography
            variant="h6"
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
            Search & Select Application
          </Typography>
          
          <Box sx={{ p: 3 }}>
            <Autocomplete
              options={searchableApps}
              value={selectedSearchApp}
              onChange={handleEnhancedApplicationChange}
              getOptionLabel={(option) => option.displayName}
              filterOptions={filterApplications}
              loading={searchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Applications"
                  placeholder="Type application name, client ID, or app type..."
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: 'action.disabled' }} />
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: 'rgba(25, 118, 210, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      {getAppTypeIcon(option.appType)}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option.displayName}
                        </Typography>
                        <Chip
                          label={option.appType}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            backgroundColor: getAudienceColor(option.signInAudience) + '20',
                            color: getAudienceColor(option.signInAudience),
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {option.appId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.signInAudience}
                        </Typography>
                        {option.hasSecrets && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Chip
                              label="Has Secrets"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.625rem',
                                backgroundColor: '#f44336',
                                color: 'white'
                              }}
                            />
                          </>
                        )}
                        {option.hasCredentials && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Chip
                              label="Has Certificates"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.625rem',
                                backgroundColor: '#ff9800',
                                color: 'white'
                              }}
                            />
                          </>
                        )}
                      </Box>
                    }
                  />
                </Box>
              )}
              noOptionsText={
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No applications found matching your search.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try searching by app name, client ID, or app type.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            {/* Search Tips Button */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              mb: selectedSearchApp ? 2 : 0
            }}>
              <Button 
                onClick={() => setShowSearchTips(!showSearchTips)}
                startIcon={<InfoOutlinedIcon fontSize="small" />}
                size="small"
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {showSearchTips ? 'Hide Search Tips' : 'Show Search Tips'}
              </Button>
            </Box>

            {/* Collapsible Search Tips */}
            {showSearchTips && (
              <Box sx={{ 
                mb: selectedSearchApp ? 2 : 0,
                p: 2, 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #e9ecef',
                borderRadius: 1,
                borderLeft: '4px solid #17a2b8'
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0c5460', mb: 1, fontWeight: 600 }}>
                  Search Tips:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Name:</strong> "MyApp" or "Test Application"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Client ID:</strong> "12345678-1234-1234-1234-123456789012"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>By Type:</strong> "Web", "SPA", "API", "Mobile"
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Combined:</strong> "MyApp Web" or "API production"
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {selectedSearchApp && (
              <Alert 
                severity="info" 
                variant="outlined" 
                sx={{ borderRadius: 2 }}
                action={
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => handleEnhancedApplicationChange({} as React.SyntheticEvent, selectedSearchApp)}
                    startIcon={<RefreshIcon />}
                    disabled={searchLoading}
                  >
                    {searchLoading ? 'Loading...' : 'Refresh Manifest'}
                  </Button>
                }
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Viewing manifest for <strong>{selectedSearchApp.displayName}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Client ID: <code>{selectedSearchApp.appId}</code>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type: {selectedSearchApp.appType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Audience: {selectedSearchApp.signInAudience}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Read-Only Notice */}
      {selectedApp && (
        <Alert 
          severity="info" 
          variant="filled"
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body2">
            This is a read-only view of the application manifest. No changes can be made through this interface.
            To modify the application configuration, use the Azure Portal or Microsoft Graph API with appropriate permissions.
          </Typography>
        </Alert>
      )}

      {/* Viewer Card - only show when an app is selected */}
      {selectedApp ? (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
                Manifest JSON (Read-Only)
              </Box>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
                sx={{
                  borderRadius: 2,
                  color: copySuccess ? 'success.main' : 'primary.main',
                  borderColor: copySuccess ? 'success.main' : 'primary.main',
                }}
              >
                {copySuccess ? "Copied!" : "Copy"}
              </Button>
            </Typography>
            
            <Box sx={{ p: 3 }}>
              <TextField
                label="Application Manifest (Read-Only)"
                variant="outlined"
                fullWidth
                multiline
                rows={20}
                value={manifestJson}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    backgroundColor: '#f9f9f9'
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, bgcolor: '#f9f9f9' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <Box 
              sx={{ 
                p: 2,
                borderRadius: '50%',
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <VisibilityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Application Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 450 }}>
              Please select an application from the dropdown above to view its manifest.
              The manifest viewer displays the application's configuration in JSON format.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9', mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About Application Manifest Viewing</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  What You Can View
                </Typography>
                <Typography variant="body2">
                  View complete application configuration including authentication settings, 
                  API permissions, app roles, redirect URIs, and other registration properties 
                  in JSON format.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                  Common Uses
                </Typography>
                <Typography variant="body2">
                  • Understanding application configuration<br />
                  • Documenting current settings<br />
                  • Copying configuration for templates<br />
                  • Troubleshooting authentication issues
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                  Making Changes
                </Typography>
                <Typography variant="body2">
                  • Use Azure Portal for UI-based editing<br />
                  • Use Microsoft Graph API for programmatic changes<br />
                  • Use PowerShell or Azure CLI for scripting<br />
                  • Ensure appropriate permissions for modifications
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ManifestViewer;